// src/components/Auth0QuickTest.tsx
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle, ExternalLink, Copy } from 'lucide-react';
import { normalizePromptParam } from '../firebase/oauth-auth';

export function Auth0QuickTest() {
  const [authUrl, setAuthUrl] = useState<string>('');
  const [visitLoginHint, setVisitLoginHint] = useState<string>('');
  const [visitOrganization, setVisitOrganization] = useState<string>('');
  const [visitProviderId, setVisitProviderId] = useState<string>('');
  const [debugSnapshot, setDebugSnapshot] = useState(() => readVisitDebugSnapshot());

  const generateAuth0Url = () => {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: '9FGFJ6Kp3d6ZyDxKCcLX1TjaurbpuqBP',
      redirect_uri: 'https://travelinauth.firebaseapp.com/__/auth/handler',
      scope: 'openid profile email read:structures manage:structures read:roles',
      state: Math.random().toString(36).substring(7),
      audience: 'https://login.visitbergamo.net/api/v2/',
      manager_structures: 'true',
      roles: 'structure_manager,portal_manager'
    });

    const url = `https://login.visitbergamo.net/authorize?${params.toString()}`;
    setAuthUrl(url);
    
    // Copy to clipboard
    navigator.clipboard.writeText(url);
    
    return url;
  };

  const openAuth0Flow = () => {
    const url = generateAuth0Url();
    window.open(url, '_blank');
  };

  const copyUrl = () => {
    if (authUrl) {
      navigator.clipboard.writeText(authUrl);
      alert('URL copiata negli appunti!');
    }
  };

  function buildVisitUrl(prompt?: string) {
    const base = new URL(window.location.origin + window.location.pathname);
    base.searchParams.set('fromVisit', '1');
    if (prompt) base.searchParams.set('prompt', prompt);
    if (visitLoginHint) base.searchParams.set('login_hint', visitLoginHint);
    if (visitOrganization) base.searchParams.set('organization', visitOrganization);
    if (visitProviderId) base.searchParams.set('providerId', visitProviderId);
    return base.toString();
  }

  function startVisitFlow(prompt?: string) {
    const url = buildVisitUrl(prompt);
    window.location.assign(url);
  }

  function readVisitDebugSnapshot() {
    try {
      const url = new URL(window.location.href);
      const promptRaw = url.searchParams.get('prompt');
      const prompt = normalizePromptParam(promptRaw);
      return {
        location: `${url.pathname}${url.search}`,
        prompt: prompt ?? 'none',
        hasRedirectParams: url.searchParams.has('code') || url.searchParams.has('state') || url.searchParams.has('error'),
        loginHint: url.searchParams.get('login_hint') || url.searchParams.get('email') || url.searchParams.get('vbEmail') || '',
        organization: url.searchParams.get('organization') || '',
        providerId: url.searchParams.get('providerId') || '',
        visitEntryFlag: sessionStorage.getItem('visit:entry') === '1',
        autoQuickStarted: sessionStorage.getItem('auth0:auto-quick-started') === '1',
        silentAttempted: sessionStorage.getItem('auth0:silent-attempted') === '1',
        silentFailed: sessionStorage.getItem('auth0:silent-failed') === '1',
        interactiveStarted: sessionStorage.getItem('auth0:interactive-started') === '1'
      };
    } catch {
      return {
        location: window.location.pathname,
        prompt: 'unknown',
        hasRedirectParams: false,
        loginHint: '',
        organization: '',
        providerId: '',
        visitEntryFlag: false,
        autoQuickStarted: false,
        silentAttempted: false,
        silentFailed: false,
        interactiveStarted: false
      };
    }
  }

  function refreshDebugSnapshot() {
    setDebugSnapshot(readVisitDebugSnapshot());
  }

  function clearVisitFlags() {
    try {
      sessionStorage.removeItem('visit:entry');
      sessionStorage.removeItem('auth0:auto-quick-started');
      sessionStorage.removeItem('auth0:silent-attempted');
      sessionStorage.removeItem('auth0:silent-failed');
      sessionStorage.removeItem('auth0:interactive-started');
    } catch {}
    try {
      const base = new URL(window.location.origin + window.location.pathname);
      window.history.replaceState(null, '', base.toString());
    } catch {}
    refreshDebugSnapshot();
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Test rapido Auth0
            <Badge variant="outline">login.visitbergamo.net</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-s">
            <div>
              <strong>Dominio:</strong> login.visitbergamo.net
            </div>
            <div>
              <strong>Client ID:</strong> 9FGFJ6Kp3d6ZyDxKCcLX1TjaurbpuqBP
            </div>
            <div>
              <strong>URI di redirect:</strong> travelinauth.firebaseapp.com/__/auth/handler
            </div>
            <div>
              <strong>Scope:</strong> openid, profile, email, read:structures, manage:structures
            </div>
          </div>

          <div className="space-y-3">
            <Button onClick={openAuth0Flow} className="w-full flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              Testa flusso login Auth0
            </Button>

            <Button variant="outline" onClick={generateAuth0Url} className="w-full">
              Genera URL di autorizzazione
            </Button>

            {authUrl && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-s font-medium">URL generata:</span>
                  <Button variant="ghost" size="sm" onClick={copyUrl}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <div className="bg-gray-50 p-2 rounded text-ss font-mono break-all max-h-20 overflow-y-auto">
                  {authUrl}
                </div>
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium text-s mb-2">Configurazione Auth0 prevista:</h4>
            <ul className="text-ss space-y-1 text-muted-foreground">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-600" />
                Tipo applicazione: Single Page Application
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-600" />
                URL di callback consentiti configurati
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-600" />
                Action/Rule personalizzate per i claims
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-600" />
                app_metadata utente configurato (portals: "Visit Bergamo")
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <div className="text-blue-600 mt-0.5">ℹ️</div>
              <div className="text-s text-blue-800">
                <strong>Passi successivi:</strong>
                <ol className="mt-1 ml-4 list-decimal space-y-1">
                  <li>Testa il flusso di login cliccando il pulsante sopra</li>
                  <li>Verifica il reindirizzamento al login Auth0</li>
                  <li>Dopo il login, controlla l'URL di redirect per il codice di autorizzazione</li>
                  <li>Usa il Test configurazione OAuth per la validazione completa</li>
                </ol>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Debug flusso Visit Bergamo
            <Badge variant="outline">SSO</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="visit-login-hint">Suggerimento login (email)</Label>
              <Input
                id="visit-login-hint"
                placeholder="utente@visitbergamo.it"
                value={visitLoginHint}
                onChange={(e) => setVisitLoginHint(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="visit-org">Organizzazione (opzionale)</Label>
              <Input
                id="visit-org"
                placeholder="org_abc123"
                value={visitOrganization}
                onChange={(e) => setVisitOrganization(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="visit-provider">Sostituisci Provider ID</Label>
              <Input
                id="visit-provider"
                placeholder="oidc.visit-bergamo"
                value={visitProviderId}
                onChange={(e) => setVisitProviderId(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button onClick={() => startVisitFlow('none')} className="w-full">
              Avvia flusso Visit (prompt=none)
            </Button>
            <Button variant="outline" onClick={() => startVisitFlow('login')} className="w-full">
              Avvia flusso Visit (prompt=login)
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={refreshDebugSnapshot}>
              Aggiorna snapshot di debug
            </Button>
            <Button variant="ghost" onClick={clearVisitFlags}>
              Pulisci flag flusso Visit
            </Button>
          </div>

          <div className="text-ss bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1">
            <div className="font-semibold text-slate-700">Snapshot corrente</div>
            <div className="text-slate-600">Percorso: {debugSnapshot.location}</div>
            <div className="text-slate-600">Prompt: {debugSnapshot.prompt}</div>
            <div className="text-slate-600">Parametri redirect: {debugSnapshot.hasRedirectParams ? 'si' : 'no'}</div>
            <div className="text-slate-600">login_hint: {debugSnapshot.loginHint || '—'}</div>
            <div className="text-slate-600">organization: {debugSnapshot.organization || '—'}</div>
            <div className="text-slate-600">providerId: {debugSnapshot.providerId || '—'}</div>
            <div className="text-slate-600">visit:entry: {debugSnapshot.visitEntryFlag ? '1' : '0'}</div>
            <div className="text-slate-600">auth0:auto-quick-started: {debugSnapshot.autoQuickStarted ? '1' : '0'}</div>
            <div className="text-slate-600">auth0:silent-attempted: {debugSnapshot.silentAttempted ? '1' : '0'}</div>
            <div className="text-slate-600">auth0:silent-failed: {debugSnapshot.silentFailed ? '1' : '0'}</div>
            <div className="text-slate-600">auth0:interactive-started: {debugSnapshot.interactiveStarted ? '1' : '0'}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}